import React, { useState, useEffect, useMemo } from "react";
import { FileUploader } from "react-drag-drop-files";
import { BsArrowRight, BsCheck, BsPencilFill } from "react-icons/bs";
import { ImBin } from "react-icons/im";
import { MdRemoveRedEye } from "react-icons/md";
import { useFormik } from "formik";
import { removeLeadingZeros } from "../../../utils/helpers/filter";
import moment from "moment";
import {
  policyApplication,
  savingsPolicyApplication,
} from "../../../utils/validators/schema";
import {
  policyValidation,
  savingsPolicyValidator,
} from "../../../utils/validators/validators";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import EditRiskCovered from "../../modals/policy/EditRiskCovered";
import PreviewPolicyAppSchedule from "../../modals/preview/PreviewPolicyAppSchedule";
import Select from "../../form/select/Select";
import { categories } from "../../../utils/data/account_type";
import useFileUpload from "../../../utils/hooks/useFileUpload";
import { forwardRef } from "react";
import {
  roundUpTens,
  schedulePolicyRepayment,
} from "../../../utils/helpers/functions";
import { useRef } from "react";
import { useSignature } from "../../../utils/hooks/useSignature";
import MultiSelect from "../../form/select/MultiSelect";
import PreviewSavingsPolicyAppSchedule from "./../../../pages/admin/customer/Savings-application/PreviewSavingsPolicyAppSchedule";
import AddOtherSavingsCover from "../../modals/policy/AddOtherSavingsCover";
import {
  deleteOtherCover,
  setDbPolicyRisksToOthers,
  setOtherCovers,
} from "../../../redux/slice/policy/savingsPolicy/savingsPolicySlice";

const SavingsPolicyInformation = forwardRef(
  (
    {
      policyDetails,
      policy_app_rsk,
      life_cover,
      setUnderWritten,
      isNext,
      setIsNext,
      death_claim
    },
    ref
  ) => {
    const fileTypes = ["JPG", "PNG", "PDF", "DOC", "DOCX"];
    const { user } = useSelector((state) => state.auth?.user);
    const { temp_policy_attach_data, risk_covered } = useSelector(
      (state) => state.policy
    );
    const {
      customer_policy_details,
      otherCovers,
      covers,
      savingsPolicyConfig,
    } = useSelector((state) => state.savingsPolicy);

    const [policyIdError, setPolicyIdError] = useState({
      error: "",
      touched: false,
    });

    const [previewData, setPreviewData] = useState(null);
    const [premiumPayment, setPremiumPayment] = useState(null);
    const [editData, setEditData] = useState(null);
    const [endDate, setEndDate] = useState(policyDetails?.endDate);
    const { fileUrl, singleFileUpload } = useFileUpload();
    const [isUploading, setIsUploading] = useState(false);    
    
    const {
      value: sign,
      Signature,
      clearSignature,
      saveSignature,
    } = useSignature();

    const dispatch = useDispatch();
    const nextRef = useRef();
    const repaymentRef = useRef();

    const handleFileChange = (file) => {
      setIsUploading(true);
      singleFileUpload(file);
    };

    const onSubmit = (values) => {
      const payload = {
        underwriter_signature: sign,
        staff_id: user?.staff_id,
        underwriter_staff_id: user?.staff_id,
        detailed_spec: values?.detailed_specification,
        savings_policy_id: policyDetails?.policy_id,
        duration: values?.duration,
        policy_app_no: policyDetails?.policyAppNumber,
        effective_date: values.effective_date,
        end_date: endDate,
        repayment_frequency: values?.repayment_frequency,
        policy_app_approval_date: moment().format("YYYY-MM-DD"),
        proposal_document: values?.proposal_document,
        sum_assured: values?.sum_assured,
        total_premium: totalPremium,
        interest_rate: values?.interest_rate,
        customer_id: policyDetails?.customerId,
        customer_age: policyDetails?.customer_age || 18,
        premuim_contribution: premiumPayment,
        covers: otherCovers,
      };

      setUnderWritten(payload);
      ref.current?.click();
    };

    const {
      values,
      errors,
      touched,
      handleBlur,
      handleChange,
      handleSubmit,
      setFieldValue,
      setFieldTouched,
    } = useFormik({
      enableReinitialize: true,
      initialValues: {
        duration: policyDetails?.duration,
        effective_date: moment(policyDetails?.effectiveDate).format(
          "YYYY-MM-DD"
        ),
        repayment_frequency: policyDetails?.repayment_frequency,
        sum_assured: policyDetails?.sumAssured,
        proposal_document: "",
        interest_rate: parseFloat(policyDetails?.interest_rate),
      },
      validationSchema: savingsPolicyApplication,
      validate: (values) =>
        savingsPolicyValidator(
          values,
          savingsPolicyConfig?.data?.p_gen_info[0]?.minimum_duration,
          savingsPolicyConfig?.data?.rate
        ),
      onSubmit,
    });

    // calculate total premium
    const totalPremium = useMemo(() => {
      let totalPremium = 0;
      if (otherCovers?.length > 0) {
        let total = otherCovers?.reduce((acc, totalOtherCoverPremium) => {
          let calcVal =
            (parseFloat(totalOtherCoverPremium?.sum_assured) *
              parseFloat(totalOtherCoverPremium?.rate)) /
            100;

          return (acc += calcVal);
        }, 0);

        totalPremium = total;
      }
      setFieldValue("total_premium", totalPremium);

      return totalPremium + parseFloat(values?.sum_assured);
    }, [otherCovers, values.sum_assured]);

    // date manipulation starts here
    const calculateEndDate = () => {
      const effectiveDateObj = moment(values?.effective_date);
      const EndDateObj = effectiveDateObj
        .add(values?.duration, "months")
        .subtract(1, "days"); // Subtract 1 day
      const EndDateFormatted = EndDateObj.format("YYYY-MM-DD");
      setEndDate(EndDateFormatted);
    };

    useEffect(() => {
      if (values?.effective_date || values?.duration) {
        calculateEndDate();
      }
      if (fileUrl) {
        setFieldValue("proposal_document", fileUrl);
        setIsUploading(false);
      }
    }, [values?.effective_date, values?.duration, fileUrl]);

    useEffect(() => {
      const data = schedulePolicyRepayment(
        values?.effective_date,
        endDate,
        values?.repayment_frequency,
        totalPremium,
        values?.duration
      );
      setPremiumPayment(data[0]?.amount);
    }, [
      values?.repayment_frequency,
      values?.sum_assured,
      values?.effective_date,
      totalPremium,
    ]);

    useEffect(() => {
      setPreviewData({
        ...values,
        customer_id: policyDetails?.customerId,
        customer_name:
          policyDetails?.customerFirstName +
          " " +
          policyDetails?.customerLastName,
        agent_name:
          policyDetails?.agentFirstName + " " + policyDetails?.agentLastName,
        policy_app_number: policyDetails?.policyAppNumber,
        office_name: policyDetails?.office_name,
        end_date: endDate || policyDetails?.endDate,
      });
      setUnderWritten(values);
    }, [values, policyDetails, endDate]);

    useEffect(() => {
      if (isNext) {
        nextRef.current.click();
        setIsNext(false);
      }
    }, [isNext]);

    useEffect(() => {
      if (policy_app_rsk) {
        dispatch(setDbPolicyRisksToOthers(policy_app_rsk));
      }
    }, [policy_app_rsk]);

    return (
      <div>
        <div className='border p-3 rounded-3 my-1 position-relative'>
          <h4 className='fw-semibold text-primary-1 bg-white position-absolute profile-title'>
            Investment Information
          </h4>

          <div className='col-md-12'>
            <div className='row'>
              <div className='col-md-4'>
                <div className='mb-3'>
                  <label htmlFor='policy_name' className='form-label'>
                    Duration (in months) <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='number'
                    id='duration'
                    name='duration'
                    className={`form-control form-control-sm no-arrows ${
                      errors?.duration && touched?.duration && "is-invalid"
                    }`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values?.duration}
                    onInput={(e) => removeLeadingZeros(e.target.id)}
                    // placeholder='Enter Duration'
                  />
                  {errors?.duration && touched?.duration && (
                    <span className='text-danger'>{errors?.duration}</span>
                  )}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='mb-3'>
                  <label htmlFor='policy_name' className='form-label'>
                    Effective date <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='date'
                    id='effective_date'
                    name='effective_date'
                    className={`form-control form-control-sm no-arrows ${
                      errors?.effective_date &&
                      touched?.effective_date &&
                      "is-invalid"
                    }`}
                    value={values?.effective_date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors?.effective_date && touched?.effective_date && (
                    <span className='text-danger'>
                      {errors?.effective_date}
                    </span>
                  )}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='mb-3'>
                  <label htmlFor='policy_name' className='form-label'>
                    End date
                  </label>
                  <input
                    type='date'
                    id='end_date'
                    name='end_date'
                    defaultValue={endDate}
                    className={`form-control form-control-sm`}
                    disabled
                  />
                </div>
              </div>
              <div className='col-md-4'>
                <div className='mb-3'>
                  <label className='form-label'>
                    Repayment Frequency <span className='text-danger'>*</span>
                  </label>

                  <div>
                    <Select
                      isError={
                        errors?.repayment_frequency &&
                        touched?.repayment_frequency
                      }
                      name='repayment_frequency'
                      ref={repaymentRef}
                      data={categories?.map((item) => ({
                        label: item?.name,
                        value: item?.name,
                      }))}
                      value={values.repayment_frequency}
                      onChange={(selectedOption) =>
                        setFieldValue(
                          "repayment_frequency",
                          selectedOption?.value
                        )
                      }
                      onBlur={() => {
                        handleBlur({
                          target: { name: "repayment_frequency" },
                        });
                      }}
                    />
                    {errors?.repayment_frequency &&
                      touched?.repayment_frequency && (
                        <p className='text-danger mb-0'>
                          {errors?.repayment_frequency}
                        </p>
                      )}
                  </div>
                </div>
              </div>
              <div className='col-md-4'>
                <div className='mb-3'>
                  <label htmlFor='policy_name' className='form-label'>
                    Sum Assured
                  </label>
                  <input
                    type='number'
                    id='sum_assured'
                    name='sum_assured'
                    value={values?.sum_assured}
                    className={`form-control form-control-sm no-arrows ${
                      errors?.sum_assured &&
                      touched?.sum_assured &&
                      "is-invalid"
                    }`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors?.sum_assured && touched?.sum_assured && (
                    <span className='text-danger'>{errors?.sum_assured}</span>
                  )}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='mb-3'>
                  <label htmlFor='policy_name' className='form-label'>
                    Interest Rate
                  </label>
                  <input
                    type='number'
                    id='interest_rate'
                    name='interest_rate'
                    value={values?.interest_rate}
                    className={`form-control form-control-sm no-arrows ${
                      errors?.interest_rate &&
                      touched?.interest_rate &&
                      "is-invalid"
                    }`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors?.interest_rate && touched?.interest_rate && (
                    <span className='text-danger'>{errors?.interest_rate}</span>
                  )}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='mb-3'>
                  <label htmlFor='policy_name' className='form-label'>
                    Premium Contribution
                  </label>
                  <input
                    type='number'
                    value={
                      premiumPayment ||
                      temp_policy_attach_data?.premuim_contribution
                    }
                    className={`form-control form-control-sm no-arrows`}
                    readOnly
                  />
                </div>
              </div>
              <div className='col-md-4 mb-3'>
                <label
                  className='form-label d-block'
                  htmlFor='proposal_document'
                >
                  <span className={`${fileUrl && "text-success"}`}>
                    Upload Proposal document
                  </span>
                  {!fileUrl && isUploading && (
                    <span>
                      <i className='mdi mdi-dots-circle mdi-spin '></i>{" "}
                    </span>
                  )}
                  {fileUrl && (
                    <span>
                      <i className='mdi mdi-check text-success fs-5'></i>
                    </span>
                  )}
                </label>
                <FileUploader
                  label='Browse Document'
                  children={
                    <div
                      className='borde border-dotted p-1 px-2 text-center cursor-p'
                      style={{ border: "2px dashed grey" }}
                    >
                      <i className='uil uil-cloud-upload fs-4'></i> Upload
                      Document
                      <span className='text-primary-dark mt-0'></span>
                    </div>
                  }
                  handleChange={handleFileChange}
                  onDrop={handleFileChange}
                  name='file'
                  types={fileTypes}
                />
              </div>
              <div className='row'>
                {fileUrl ||
                temp_policy_attach_data?.proposal_document ||
                policyDetails?.proposal_document ? (
                  <div className='col-md-4' style={{ marginTop: "28px" }}>
                    <div>
                      <a
                        href={
                          fileUrl ||
                          temp_policy_attach_data?.proposal_document ||
                          policyDetails?.proposal_document
                        }
                        target='_blank'
                        className='btn btn-sm btn-primary text-white d-flex gap-2 align-items-center align-content-center justify-content-center'
                      >
                        View Proposal Document
                        <i className='mdi mdi-eye text-white fs-5'></i>
                      </a>
                    </div>
                  </div>
                ) : null}
                <div className='col-md-4 d-flex' style={{ marginTop: "28px" }}>
                  <div>
                    <span
                      className='btn btn-primary btn-sm text-white px-4 d-flex gap-2 align-items-center'
                      data-bs-toggle='modal'
                      data-bs-target='#more_document_modal'
                    >
                      <span>Preview Payment Plan</span>
                      <MdRemoveRedEye
                        className='text-white'
                        style={{
                          fontSize: "20px",
                        }}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='border p-3 rounded-3 my-3 position-relative'>
          <h4 className='fw-semibold text-primary-1 bg-white position-absolute profile-title'>
            Risk Covered Information
          </h4>

          {/* <p className=''>
                  {covers ? covers?.get_life_covers[0]?.life_cover_name : ""}
                </p> */}
          <p>
            <span className='fw-bold'>Life Cover Name: </span>
            {covers?.get_life_covers.length &&
              covers?.get_life_covers[0]?.life_cover_name}
          </p>
          <p>
          <span className='fw-bold'>Death Benefit Claim Name: </span>
          {death_claim?.life_cover_claim_name === "multiple" && (
            <>
              <span>Multiple</span>
            </>
          )}
          {death_claim?.life_cover_claim_name === "assumed" && (
            <>
              <span>Assumed Sum Assured</span>
            </>
          )}
          {death_claim?.life_cover_claim_name === "guaranteed" && (
            <>
              <span>Guaranteed Sum Assured</span>
            </>
          )}
        </p>
          {death_claim?.life_cover_claim_name === "guaranteed" && <p>
          <span className='fw-bold'>Guaranteed Type: </span>
          {death_claim?.assumed_garranty_type === "fixed" && (
            <>
              <span>Fixed</span>
            </>
          )}
          {death_claim?.assumed_garranty_type === "range" && (
            <>
              <span>Range</span>
            </>
          )}
        </p>}
          {death_claim?.life_cover_claim_name === "multiple" && <p>
            <span className='fw-bold'>Death Benefit Amount: </span>
            {JSON.parse(death_claim?.multiple_value)?.map((item, i) => (
                          <span
                            className='bg-primary text-light fs-6 text-capitalize'
                            style={{ padding: "4px", margin: "0px 4px" }}
                            key={i}
                          >
                            {item}
                          </span>
                      ))}
          </p>} {death_claim?.life_cover_claim_name === "multiple" &&<br />}
          {death_claim?.life_cover_claim_name === "guaranteed" && 
            <div className="mt-2">
              {death_claim?.assumed_garranty_type === "fixed" && <p>
                <span className='fw-bold'>Death Benefit Amount: </span>
                <span className="">{death_claim?.assumed_garranty_value}</span>
              </p>}
              {death_claim?.assumed_garranty_type === "range" && <p>
                <span className='fw-bold'>Guaranteed Minimum Amount: </span>
                {death_claim?.assumed_garranty_minimum}
                <span className='fw-bold ms-2'>Guaranteed Maximum Amount: </span>
                {death_claim?.assumed_garranty_maximum}
              </p>}
            </div> 
          }

          <button
            className='btn btn-sm btn-primary'
            data-bs-toggle='modal'
            data-bs-target='#other-cover1'
            type='button'
            onClick={() => setEditData(null)}
          >
            Add Other Cover
          </button>

          <div className='col-md-12 mb-3 mx- mt-2 table-responsive-sm'>
            {otherCovers?.length ? (
              <table className='table table-centered table-striped nowrap w-100 no-footer'>
                <thead>
                  <tr>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "140.8px" }}
                      aria-label='Customer: activate to sort column ascending'
                    >
                      Other Cover
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "5.8px" }}
                      aria-label='Customer: activate to sort column ascending'
                    >
                      Rate(%)
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "140.8px" }}
                      aria-label='Phone: activate to sort column ascending'
                    >
                      sum Assured
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "147.8px" }}
                    >
                      Premium
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "257.8px" }}
                      aria-label='Email: activate to sort column ascending'
                    >
                      Detail Description
                    </th>

                    <th
                      className='sorting sorting_asc'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "146.8px" }}
                    ></th>
                  </tr>
                </thead>

                <tbody>
                  {/* {policy_app_rsk?.map((item, i) => (
                    <tr key={i}>
                      <td className='text-capitalize'>
                        {
                          covers?.get_other_cover?.filter(
                            (p) => p.id === item?.cover_id
                          )[0]?.cover_name
                        }
                      </td>
                      <td className='table-user'>{item?.rate}</td>
                      <td className='table-user'>{item?.sum_assured}</td>
                      <td className='text-dark'>{item?.premium}</td>
                      <td>{item?.desc}</td>

                      <td className='text-center'>
                        <div className='d-flex gap-2 align-content-center justify-content-center'>
                          <span
                            className='text-primary cursor-p'
                            data-bs-toggle='modal'
                            data-bs-target='#other-cover1'
                            onClick={() => setEditData(item)}
                          >
                            <BsPencilFill size={17} />
                          </span>
                          <span
                            className='text-danger cursor-p'
                            onClick={() =>
                              dispatch(deleteOtherCover(item?.cover_id))
                            }
                          >
                            <ImBin size={17} />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))} */}
                  {otherCovers?.map((item, i) => (
                    <tr key={i}>
                      <td className='text-capitalize'>
                        {
                          covers?.get_other_cover?.filter(
                            (p) => p.id === item?.cover_id
                          )[0]?.cover_name
                        }
                      </td>
                      <td className='table-user'>{item?.rate}</td>
                      <td className='table-user'>{item?.sum_assured}</td>
                      <td className='text-dark'>{item?.premium}</td>
                      <td>{item?.desc}</td>

                      <td className='text-center'>
                        <div className='d-flex gap-2 align-content-center justify-content-center'>
                          <span
                            className='text-primary cursor-p'
                            data-bs-toggle='modal'
                            data-bs-target='#other-cover1'
                            onClick={() => setEditData(item)}
                          >
                            <BsPencilFill size={17} />
                          </span>
                          <span
                            className='text-danger cursor-p'
                            onClick={() =>
                              dispatch(deleteOtherCover(item?.cover_id))
                            }
                          >
                            <ImBin size={17} />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            <div className='row mb-3 mt-2'>
              <div className='col-md-12'>
                <label className='form-label'>Detailed Specifications</label>
                <textarea
                  id='detailed_specification'
                  name='detailed_specification'
                  value={values?.detailed_specification}
                  className={`form-control no-arrows`}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className='border p-3 rounded-3 my-3 position-relative'
          onSubmit={() => {}}
        >
          <h4 className='fw-semibold text-primary-1 bg-white position-absolute profile-title'>
            Premium Information
          </h4>

          <div className='col-md-4'>
            <div className='mb-3'>
              <label htmlFor='policy_name' className='form-label'>
                Total Premium
              </label>
              <input
                type='text'
                id='total_premium'
                name='total_premium'
                className={`form-control form-control-sm`}
                value={totalPremium}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className='col-md-12'>
          <div className='row mb-2'>
            <div className='col-md-6 mt-2'>
              <label htmlFor='inputEmail4' className='form-label d-block'>
                Underwriter's signature
                {sign && (
                  <span className='text-success'>
                    <BsCheck size={22} />
                  </span>
                )}
              </label>
              <div className='d-flex gap-3'>{Signature}</div>
              <div className='d-flex gap-2 mt-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-secondary'
                  onClick={() => clearSignature()}
                >
                  Clear signature
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-info'
                  onClick={() => saveSignature()}
                >
                  Sign
                </button>
              </div>
            </div>
          </div>
          <div className='d-flex justify-content-end mb-2'>
            <button
              type='submit'
              ref={nextRef}
              className='btn btn-sm btn-primary d-flex gap-1 align-items-center'
              onClick={() => {
                handleSubmit(values);
              }}
            >
              <span>Next</span>
              <BsArrowRight />
            </button>
          </div>
        </div>

        <PreviewSavingsPolicyAppSchedule
          modal_id='more_document_modal'
          edit
          data={previewData}
          // setPartPremium={setPartPremium}
        />

        {/* <AddOtherSavingsCover
          modal_id='other-cover1'
          editData={editData}
          setEditData={setEditData}
        /> */}

        <AddOtherSavingsCover
          modal_id='other-cover1'
          editData={editData}
          setEditData={setEditData}
          selectedCovers={otherCovers}
        />
      </div>
    );
  }
);

export default SavingsPolicyInformation;
